import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import ChevronSVG from '../../../assets/icons/chevron.svg';
import DownloadIconSVG from '../../../assets/icons/download-icon.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ActionButton } from 'src/commons/components/Button';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import Heading from 'src/commons/components/Heading';
import {
  ApplicationStatus,
  colors,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import {
  GraphQLResponse,
  ApplicationLegalDocument,
} from 'src/commons/data/graphql';
import { getCreditCardApplicationDocuments } from 'src/commons/data/queries';
import { formatDocumentDate } from 'src/commons/utilities/formatting';
import { logError } from 'src/commons/utilities/logging';
import { goToDashaboard, prefixRoute } from 'src/commons/utilities/routes';

// Interfaces -------------------------------------------------------------------------------------

interface Props {
  title: ApplicationLegalDocument['title'];
}

// #region -- Styles ------------------------------------------------------------------------------
const StyledCard = styled(Card)`
  align-items: flex-start;
`;
StyledCard.displayName = 'StyledCard';

const AgreementsSubHeading = styled.h2`
  margin: 2.75rem auto 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #bdbdbd;
  text-transform: uppercase;
`;
AgreementsSubHeading.displayName = 'AgreementsSubHeading';

const AgreementsHeading = styled(Heading)`
  margin: 0 auto 1.5rem;
  padding: 0 1.125rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 20rem;
    padding: 0 0.5rem;
  }
`;
AgreementsHeading.displayName = 'AgreementsHeading';

const TitleRow = styled.div`
  height: 3.375rem;
  width: 100%;
  border-bottom: 0.0625rem solid ${colors.BORDER_SECONDARY};
  display: flex;
  align-items: center;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    height: 0;
    margin: 0.75rem 0 0;
  }
`;
TitleRow.displayName = 'TitleRow';

const DocumentRow = styled.div`
  width: 100%;
  min-height: 3.375rem;
  border-bottom: 0.0625rem solid ${colors.BORDER_SECONDARY};
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    min-height: 5rem;
    padding: 0.75rem 1rem;
    display: flex;
  }
`;
DocumentRow.displayName = 'DocumentRow';

const LightGreySubHeading = styled.h3`
  margin: 0.5rem 0;
  width: 100%;
  font-size: 0.875rem;
  text-align: center;
  color: #979797;
`;

const DocumentSubSectionHeading = styled(LightGreySubHeading)`
  width: 8rem;
  font-size: 0.875rem;
  text-align: left;
  color: #979797;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    display: none;
  }
`;
DocumentSubSectionHeading.displayName = 'DocumentSubSectionHeading';

const DocumentSubSection = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;
DocumentSubSection.displayName = 'DocumentSubSection';

const DocumentDateText = styled.p`
  width: 8rem;
  margin: 0.25rem 0;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${TABLET_BREAKPOINT}) {
    font-size: 0.875rem;
  }
`;
DocumentDateText.displayName = 'DocumentDateText';

const DocumentLink = styled.a`
  margin: 0.25rem 0;
  line-height: 1.375;
  text-decoration: none;
  color: ${colors.TEXT_ACTION};
  & > span {
    display: inline-block;
  }
`;
DocumentLink.displayName = 'DocumentLink';

const DocumentChevronSVG = styled(ChevronSVG)`
  width: 0.5rem;
  margin: 0 0 0.025rem 0.25rem;
  path {
    stroke: ${colors.UI_PRIMARY_ACCENT};
    stroke-width: 3;
  }
  transform: rotate(270deg);
`;
DocumentChevronSVG.displayName = 'DocumentChevronSVG';

const DocumentDownloadArrow = styled.a`
  margin: 0.25rem 0.5rem 0;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0.25rem 0 0 0.5rem;
  }
`;
DocumentDownloadArrow.displayName = 'DocumentDownloadArrow';

const AppResumeButton = styled(ActionButton)`
  margin: 2.0625rem auto 2.9375rem;
`;
AppResumeButton.displayName = 'AppResumeButton';

const ApplicationCompletedSubHeading = styled(LightGreySubHeading)`
  margin: 0 0 1.25rem;
`;
ApplicationCompletedSubHeading.displayName = 'ApplicationCompletedSubHeading';

const AppCompletedButton = styled(ActionButton)`
  margin: 2.0625rem auto 0.9375rem;
`;
AppCompletedButton.displayName = 'AppCompletedButton';
// #endregion

// When the screen width forces the carat by the document title to drop to the next line,
// we want at least the last word to drop down with it
const DocumentTitle = ({ title }: Props) => {
  const titleWithoutLastWord = (str: string) => {
    const titleString = str.split(' ');
    return titleString.slice(0, titleString.length - 1).join(' ');
  };
  const titleLastWord = (str: string) => str.split(' ').pop();
  return (
    <>
      {titleWithoutLastWord(title)}{' '}
      <span>
        {titleLastWord(title)}
        <DocumentChevronSVG />
      </span>
    </>
  );
};

export const Agreements = () => {
  // State -----------------------------------------------------------------------------------------
  const [fetchingLegalDocs, setFetchingLegalDocs] = useState(true);
  const [showFailure, setShowFailure] = useState(false);
  const [legalAgreements, setLegalAgreements] = useState([]);
  const [showResume, setShowResume] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const { applicationId } = useParams();
  const navigate = useNavigate();

  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchCreditCardApplication,
    fetchingApplication,
    showApplicationFetchError,
  } = useContext(CreditCardApplicationContext);

  const sortByCreatedDate = (documents: ApplicationLegalDocument[]) => {
    if (documents.length < 2) return documents;

    return documents.sort((docA, docB) => {
      const dateA = new Date(docA.createdDate).valueOf();
      const dateB = new Date(docB.createdDate).valueOf();
      return dateB - dateA;
    });
  };

  // Hooks ----------------------------------------------------------------------------------------
  useEffect(() => {
    fetchCreditCardApplication();

    const fetchLegalAgreements = async () => {
      try {
        // fetch legal docs
        const response: GraphQLResponse =
          await getCreditCardApplicationDocuments(applicationId);
        const { data, errors } = response;
        if (data && data.getCreditCardApplicationDocuments) {
          // sort legal docs by created date
          const sortedDocuments = sortByCreatedDate(
            data.getCreditCardApplicationDocuments
          );
          setLegalAgreements(sortedDocuments);
        }
        if (errors) {
          setShowFailure(true);
          setFetchingLegalDocs(false);
        }
      } catch (err: any) {
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          setShowFailure(true);
          setFetchingLegalDocs(false);
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'Agreements',
            function: 'fetchLegalAgreements',
          });
        }
      }
    };
    fetchLegalAgreements();
  }, []);

  // show loading card until legal agreements are set in state
  useEffect(() => {
    if (legalAgreements.length > 0) {
      setFetchingLegalDocs(false);
    }
  }, [legalAgreements]);

  useEffect(() => {
    const { applicationStatus } = creditCardApplication;
    const appResumeStatuses = [
      ApplicationStatus.OFFER_IN_PROGRESS,
      ApplicationStatus.OFFER_ERROR,
      ApplicationStatus.PENDING_OFFER_ACCEPT,
      ApplicationStatus.PENDING_BANK_CONNECT_AUTHORIZATION,
      ApplicationStatus.PENDING_BANK_CONNECT,
      ApplicationStatus.VERIFICATION_IN_PROGRESS,
      ApplicationStatus.VERIFICATION_ERROR,
      ApplicationStatus.PENDING_SUBMISSION,
    ];

    if (appResumeStatuses.includes(applicationStatus)) {
      setShowDashboard(false);
      setShowResume(true);
    }
    if (applicationStatus === ApplicationStatus.APPLICATION_COMPLETED) {
      setShowResume(false);
      setShowDashboard(true);
    }
  }, [creditCardApplication]);

  // Render ---------------------------------------------------------------------------------------

  if (fetchingLegalDocs || fetchingApplication) {
    return <LoadingCard />;
  }

  if (showFailure || showApplicationFetchError) {
    return <ErrorCard />;
  }

  return (
    <StyledCard>
      <AgreementsSubHeading>
        Prosper
        <sup>®</sup> Card
      </AgreementsSubHeading>
      <AgreementsHeading>
        Your legal agreements and disclosures
      </AgreementsHeading>
      <TitleRow>
        <DocumentSubSectionHeading>Date</DocumentSubSectionHeading>
        <DocumentSubSectionHeading>Document name</DocumentSubSectionHeading>
      </TitleRow>
      {legalAgreements.length &&
        legalAgreements.map((document: ApplicationLegalDocument) => (
          <DocumentRow key={document.documentId}>
            <DocumentSubSection>
              <DocumentDateText>
                {formatDocumentDate(new Date(document.createdDate))}
              </DocumentDateText>
              <DocumentLink target="_blank" href={document.signedViewUrl}>
                <DocumentTitle title={document.title} />
              </DocumentLink>
            </DocumentSubSection>
            <DocumentDownloadArrow
              target="_blank"
              href={document.signedDownloadUrl}
              aria-label={`Download ${document.title}`}
              download={document.title}
            >
              <DownloadIconSVG />
            </DocumentDownloadArrow>
          </DocumentRow>
        ))}
      {showResume && (
        <AppResumeButton
          id="resume-button"
          aria-label="Resume your application"
          type="button"
          onClick={() => navigate(prefixRoute(`/application/${applicationId}`))}
        >
          Resume application
        </AppResumeButton>
      )}
      {showDashboard && (
        <>
          <AppCompletedButton
            id="dashboard-button"
            aria-label="Go to your Dashboard"
            type="button"
            onClick={() => {
              window.location.href = goToDashaboard();
            }}
          >
            Go to your Dashboard
          </AppCompletedButton>
          <ApplicationCompletedSubHeading id="fiserv-sign-in-prompt">
            You'll be prompted to sign in
          </ApplicationCompletedSubHeading>
        </>
      )}
    </StyledCard>
  );
};
